import { createStyles, makeStyles, Theme, Tooltip } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

import { QuestionStatus } from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { formatDisplayedDate, formatFullDate } from '../../../../utils/commons';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { PostAuthor } from '../../common/PostAuthor';
import { PostVisibility } from '../../common/PostVisibility';

type Props = {
  className?: string;
  plexName: string;
  publishStatus: QuestionStatus;
  authorAvatar?: string;
  authorName: string;
  authorTitle: string | null | undefined;
  publishedDate: number | string;
  showUnreadBadge: boolean;
  hasAnswer: boolean;
};

export function QuestionHeader(props: Props): React.ReactElement {
  const {
    plexName,
    publishStatus,
    authorAvatar,
    authorName,
    authorTitle,
    publishedDate,
    className,
    showUnreadBadge,
    hasAnswer,
  } = props;
  const classes = useStyles();

  const datePrefix = ((): string => {
    if (!hasAnswer) {
      return 'asked on • ';
    }
    if (publishStatus === QuestionStatus.Published) {
      return 'published on • ';
    } else {
      return 'updated on • ';
    }
  })();

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.headerLeft}>
        <PostAuthor authorName={authorName} authorAvatar={authorAvatar} authorTitle={authorTitle} />
        {showUnreadBadge && (
          <Tooltip title={'This was added recently'} enterDelay={2000}>
            <PlexTypography className={classes.unreadBadge} text={'•'} size={TextSizesEnum.XXLarge} />
          </Tooltip>
        )}
      </div>
      <div className={classes.headerRight}>
        <Tooltip title={`${datePrefix}${formatFullDate(new Date(publishedDate))}`}>
          <PlexTypography
            className={classNames(classes.date)}
            text={formatDisplayedDate(new Date(publishedDate))}
            size={TextSizesEnum.Small}
          />
        </Tooltip>
        <div className={classes.publishStatus}>
          <PostVisibility plexPublishStatus={publishStatus} plexName={plexName} />
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: spacings.xlarge,
      paddingTop: spacings.normal,
      paddingBottom: spacings.normal,
    },
    plexName: {
      marginRight: spacings.small,
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerRight: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    publishStatus: {
      display: 'flex',
      color: colorPalette.purpleGray._500,
      fontSize: theme.typography.fontSize,
      marginLeft: spacings.small,
    },
    unreadBadge: {
      color: colorPalette.red._400,
      fontWeight: 'bold',
      paddingBottom: 2,
      marginRight: spacings.small,
      marginLeft: spacings.small,
      lineHeight: '8px',
    },
    date: {
      color: colorPalette.purpleGray._300,
      fontWeight: 500,
      marginLeft: spacings.xsmall,
    },
  })
);
